import { z, type output } from 'zod'
import { normalizePhoneInput } from '@app/util/helpers'
import { NUMBER_OF_HOMES, PLOT_STATUS } from '@schema/initiative'

const INITIATIVE_TITLE_MIN_LENGTH = 6

export const DESCRIPTION_MIN_LENGTH = 10
export const VISION_MIN_LENGTH = 10

export const createNameAndSloganSchema = (
  titleMinLengthMessage: string,
  descriptionMinLengthMessage: string,
) =>
  z.object({
    title: z
      .string()
      .trim()
      .min(INITIATIVE_TITLE_MIN_LENGTH, titleMinLengthMessage),
    description: z
      .string()
      .trim()
      .min(DESCRIPTION_MIN_LENGTH, descriptionMinLengthMessage),
  })

export type NameAndSloganFormValues = output<
  ReturnType<typeof createNameAndSloganSchema>
>

/** Same pattern as slogan (`description`): trimmed string must meet min length, including empty → invalid. */
export const createVisionSchema = (minLengthMessage: string) =>
  z.object({
    vision: z.string().trim().min(VISION_MIN_LENGTH, minLengthMessage),
  })

export type VisionFormValues = output<ReturnType<typeof createVisionSchema>>

/**
 * Onboarding: title + slogan + vision. Composed from {@link createNameAndSloganSchema} and
 * {@link createVisionSchema} via object shape spread (avoids deprecated Zod object `merge`).
 */
export const createMainDetailsTextSchema = (
  titleMinLengthMessage: string,
  descriptionMinLengthMessage: string,
  visionMinLengthMessage: string,
) =>
  z.object({
    ...createNameAndSloganSchema(
      titleMinLengthMessage,
      descriptionMinLengthMessage,
    ).shape,
    ...createVisionSchema(visionMinLengthMessage).shape,
  })

export type MainDetailsTextFormValues = output<
  ReturnType<typeof createMainDetailsTextSchema>
>

const membersParametersLocationOptionSchema = z.object({
  key: z.number(),
  value: z.string(),
})

const addMembersParametersRegionRequired = (
  data: { region: { key: number; value: string } },
  ctx: z.RefinementCtx,
  regionRequiredMessage: string,
): void => {
  if (data.region.key <= 0) {
    ctx.addIssue({
      code: 'custom',
      message: regionRequiredMessage,
      path: ['region'],
    })
  }
}

/** Region is the only validated field for members parameters (onboarding + edit). */
export const createMembersParametersRegionSchema = (
  regionRequiredMessage: string,
) =>
  z
    .object({
      region: membersParametersLocationOptionSchema,
    })
    .superRefine((data, ctx) =>
      addMembersParametersRegionRequired(data, ctx, regionRequiredMessage),
    )

export type MembersParametersRegionFormValues = output<
  ReturnType<typeof createMembersParametersRegionSchema>
>

/** Full members-parameters step shape; only `region` is required (see superRefine). */
export const createMembersParametersFormSchema = (
  regionRequiredMessage: string,
) =>
  z
    .object({
      searchingForMembers: z.boolean(),
      searchingForMembersDescription: z.string(),
      numberOfHomes: z.nativeEnum(NUMBER_OF_HOMES).nullish(),
      projectSize: z.string(),
      region: membersParametersLocationOptionSchema,
      locality: membersParametersLocationOptionSchema,
    })
    .superRefine((data, ctx) =>
      addMembersParametersRegionRequired(data, ctx, regionRequiredMessage),
    )

export type MembersParametersFormValues = output<
  ReturnType<typeof createMembersParametersFormSchema>
>

const POTENTIAL_PLOT_ADDRESS_MIN_LENGTH = 6

export const createPotentialPlotAddressSchema = (minLengthMessage: string) =>
  z.object({
    potentialPlotAddress: z
      .string()
      .trim()
      .min(POTENTIAL_PLOT_ADDRESS_MIN_LENGTH, minLengthMessage),
  })

export type PotentialPlotAddressFormValues = output<
  ReturnType<typeof createPotentialPlotAddressSchema>
>

export const createLegalInfoFormSchema = (addressMinLengthMessage: string) =>
  z
    .object({
      potentialPlotStatus: z.enum([
        PLOT_STATUS.underOption,
        PLOT_STATUS.noPlot,
        PLOT_STATUS.potentialPlotInMind,
      ]),
      potentialPlotAddress: z.string(),
      permit: z.boolean(),
      financingRequirementsConstruction: z.boolean(),
      financingRequirementsPlanning: z.boolean(),
      kvkRegistration: z.boolean(),
    })
    .superRefine((data, ctx) => {
      const plotQuestions = data.potentialPlotStatus !== PLOT_STATUS.noPlot
      if (plotQuestions && data.potentialPlotAddress.trim().length <= 5) {
        ctx.addIssue({
          code: 'custom',
          message: addressMinLengthMessage,
          path: ['potentialPlotAddress'],
        })
      }
    })

export type LegalInfoFormValues = output<
  ReturnType<typeof createLegalInfoFormSchema>
>

export const createContactDetailsSchema = (messages: {
  required: string
  invalidEmail: string
  phoneMinLength: string
}) =>
  z.object({
    contactFirstName: z.string().trim().min(1, messages.required),
    contactLastName: z.string().trim().min(1, messages.required),
    contactEmail: z
      .string()
      .trim()
      .pipe(z.email({ error: messages.invalidEmail })),
    contactPhoneNumber: z
      .string()
      .transform((s) => normalizePhoneInput(s))
      .refine((s) => s.length > 8, { message: messages.phoneMinLength }),
  })

export type ContactDetailsFormValues = output<
  ReturnType<typeof createContactDetailsSchema>
>
